import * as React from 'react';
import { useForm } from 'react-hook-form';
import { SheetIcon } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { type Filters } from 'hooks/store/loading-orders';
import { useCarrierOptions, useCompanyOptions } from 'hooks/api/options';
import { MultiSelectGs, InputGs } from 'components/ui/Forms';
import DateRangePickerGs from 'components/ui/Forms/dateRangePickerGs';
import { Button as ButtonGs } from 'components/ui/ButtonGs';
import { PageHeaderWithFiltersContext } from 'components/ui/page-header-with-filters';
import { Form, FormField } from 'components/ui/form';

import {
  RefineReportParamsDrawer,
  RefineReportParamsDrawerRef,
} from './refine-report-params-drawer';
import { formSchema, type FormSchema } from './formSchema';
import { createReportParams, createFilterParams } from './utils';

type Props = React.HTMLAttributes<HTMLDivElement> & {
  onSubmitFilters: (filters: Filters) => void;
  onResetFilters: () => void;
  onStatusesChange: (statuses: string[]) => void;
  onRequestReport: (reportParams: Record<string, unknown>) => void;
  orderType: '1' | '2';
};

export const FiltersForm = ({
  onSubmitFilters,
  onResetFilters,
  onStatusesChange,
  onRequestReport,
  orderType,
}: Props) => {
  const { selectedCheckboxValues, resetSelectedCheckboxValues } =
    React.useContext(PageHeaderWithFiltersContext);
  const carrierOptions = useCarrierOptions();
  const companyOptions = useCompanyOptions();

  const refineReportDrawerRef = React.useRef<RefineReportParamsDrawerRef>(null);

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const onFormSubmit = React.useCallback(
    (formData: FormSchema) => {
      const filtersParams = createFilterParams({
        formData,
        statuses: selectedCheckboxValues,
      });
      onSubmitFilters(filtersParams);
    },
    [onSubmitFilters, selectedCheckboxValues]
  );

  const onFormReset = React.useCallback(() => {
    form.reset(
      {
        orderId: '',
        carrierIds: [],
        companyIds: [],
        date: undefined,
      },
      {
        keepValues: false,
      }
    );
    resetSelectedCheckboxValues();
    onResetFilters();
  }, [form]);

  const onRefineReportDrawerSubmit = React.useCallback(
    (columns: string[]) => {
      const reportParams = createReportParams({
        formData: form.getValues(),
        statuses: selectedCheckboxValues,
        columns,
      });
      onRequestReport(reportParams);
    },
    [form, selectedCheckboxValues, onRequestReport]
  );

  React.useEffect(() => {
    onStatusesChange(selectedCheckboxValues);
  }, [onStatusesChange, selectedCheckboxValues]);

  return (
    <>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onFormSubmit)}
          className={'flex flex-col w-full'}
        >
          <div
            className={'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4'}
          >
            <FormField
              control={form.control}
              name={'orderId'}
              render={({ field }) => (
                <InputGs {...field} type="text" label={'ID'} />
              )}
            />
            <FormField
              control={form.control}
              name={'nfNumber'}
              render={({ field }) => (
                <InputGs {...field} type="text" label={'NF'} />
              )}
            />
            <FormField
              control={form.control}
              name={'date'}
              render={({ field }) => (
                <DateRangePickerGs
                  label={'Data de agendamento'}
                  onSelect={field.onChange}
                  selected={{
                    from: field.value?.from,
                    to: field.value?.to,
                  }}
                />
              )}
            />
            <FormField
              control={form.control}
              name={'companyIds'}
              render={({ field }) => (
                <MultiSelectGs
                  form={form}
                  fieldName={field.name}
                  fieldValue={field.value}
                  label={'Clientes'}
                  maxCount={1}
                  options={companyOptions.multiSelectOptions}
                  classNames={{
                    selectedBadge:
                      'max-w-[400px] md:max-w-[200px] xl:max-w-[240px]',
                  }}
                />
              )}
            />
            <FormField
              control={form.control}
              name={'carrierIds'}
              render={({ field }) => (
                <MultiSelectGs
                  form={form}
                  fieldName={field.name}
                  fieldValue={field.value}
                  label={'Transportadoras'}
                  maxCount={1}
                  options={carrierOptions.multiSelectOptions}
                  classNames={{
                    selectedBadge:
                      'max-w-[400px] md:max-w-[200px] xl:max-w-[240px]',
                  }}
                />
              )}
            />
          </div>
          <div className={'flex gap-4 mt-4'}>
            <ButtonGs.Root
              type="button"
              variant="secondary"
              size="sm"
              onClick={onFormReset}
            >
              Limpar filtros
            </ButtonGs.Root>
            <ButtonGs.Root type="submit" size="sm">
              Filtrar
            </ButtonGs.Root>
            <ButtonGs.Root
              type="button"
              size="sm"
              className="ml-auto"
              onClick={() => refineReportDrawerRef.current?.open()}
            >
              <SheetIcon />
              <span>Exportar</span>
            </ButtonGs.Root>
          </div>
        </form>
      </Form>
      <RefineReportParamsDrawer
        ref={refineReportDrawerRef}
        onSubmit={onRefineReportDrawerSubmit}
        orderType={orderType}
      />
    </>
  );
};
